/**
 * Beat 12, mark 2: Holly holds the gate (`docs/beat-12-plan.md`, stage 4).
 *
 * She waits in the paddock beyond the open vehicle gate, where beat 10 left the
 * two of them. `F` near the race gate calls her: she comes in through the
 * vehicle gate, crosses to the race gate, rears against its leaf, swings it
 * onto the yard and **holds it until the beat ends**. She does not come back to
 * Rosalie — beat 13 opens on her standing there, and that is the point.
 *
 * Pure fixed-step simulation over `Companion`, the same contract beat 9 and
 * beat 10 use: authored points and volumes, no scene. The leaf's swing and her
 * rear are ONE clock (`leafProgress`, D-157) — the room poses the pivot, swaps
 * the leaf's collider and samples her reach clip from the same number.
 */

import { Companion, type CompanionObstacle, type CompanionSnapshot, type Vec2 } from '@/systems/companion';
import { CompanionBell, type CompanionBellEvent } from '@/systems/companionBell';
import { HOLLY_BODY_RADIUS } from '@/models/holly';
import { HOLLY_BELL_STRIDE_METRES, bellEmissionRadius } from '@/audio/sounds/bell';
import { ARRIVAL_RADIUS, MOVE_SPEED, SMOOTHING, STANDOFF_RADIUS } from '@/story/act2/twelveOut';
import { LATCH_REACH_SECONDS } from '@/story/act2/woolShedPuzzle';
import { companionObstacleFromSolid } from '@/world/woolShed';
import {
  loadingYardSolids, RACE_GATE_AT, RACE_GATE_OPEN_YAW, raceGateLeafSolid, raceGateLeafTip, VEHICLE_GATE, YARD_DAWN_PADDOCK,
} from '@/world/loadingYard';

/**
 * Her lie-up: in the paddock grass a few metres past the gate, facing back
 * into the yard, so she is in the gateway's frame from most of the apron.
 */
export const HOLLY_DAWN_LIE_UP = { x: VEHICLE_GATE.centreX + 0.5, z: YARD_DAWN_PADDOCK.minZ + 3.6, yaw: Math.PI } as const;

/**
 * Just inside the gateway: companion routing only slides around solids and
 * cannot find a gap in a fence, so a call from the paddock goes through here
 * first (beat 10's `gap-north` for the same reason).
 */
const GATEWAY = { x: VEHICLE_GATE.centreX, z: VEHICLE_GATE.z - 1.2 } as const;

/**
 * Where she stands to hold the gate: north of the open leaf's free end, facing
 * back down it, so her rear puts her forefeet on the leaf's far upright.
 * Derived from the leaf's own open pose, so re-hanging the gate moves her.
 *
 * Not beside the leaf: west of it is the trailer's rear corner (0.3 m from
 * where her body would be), and east of it is the race mouth, which is where
 * the herd comes out.
 */
const OPEN_TIP = raceGateLeafTip(RACE_GATE_OPEN_YAW);
export const HOLLY_HOLD_STATION = { x: OPEN_TIP.x, z: OPEN_TIP.z + 0.8, yaw: Math.PI } as const;

/**
 * How near the race gate `F` has to be pressed to call her. The plan's *"`F`
 * near the race gate"*: the call is a thing she does AT the gate, where the
 * crew watch until mark 1 turns them — it is not a remote control.
 */
export const CALL_RANGE = 6;

/** The rear and the swing: beat 9's reach, at the same length, because it is the same gesture. */
export const LEAF_SWING_SECONDS = LATCH_REACH_SECONDS;

export const DAWN_LEAF_SHUT_ID = 'dawn-race-gate-leaf-shut';
export const DAWN_LEAF_OPEN_ID = 'dawn-race-gate-leaf-open';

export type DawnHollyPhase = 'lie-up' | 'coming' | 'rising' | 'holding';

export interface DawnHollySnapshot {
  readonly holly: CompanionSnapshot;
  readonly phase: DawnHollyPhase;
  /** 0..1: her rear and the leaf's swing, one clock. 1 is held. */
  readonly leafProgress: number;
}

/** Every yard solid she must walk round, plus both poses of the leaf; which leaf is solid is `DawnHolly`'s call. */
export function yardDawnCompanionObstacles(): CompanionObstacle[] {
  const solids = [
    ...loadingYardSolids({ dawn: true }),
    raceGateLeafSolid(0, DAWN_LEAF_SHUT_ID),
    raceGateLeafSolid(RACE_GATE_OPEN_YAW, DAWN_LEAF_OPEN_ID),
  ];
  return solids.flatMap((solid) => {
    const obstacle = companionObstacleFromSolid(solid);
    return obstacle === null ? [] : [obstacle];
  });
}

const TIME_EPSILON = 0.000000001;

function companionAt(obstacles: readonly CompanionObstacle[], at: { x: number; z: number; yaw: number }, phase: 'following' | 'at-high-latch'): Companion {
  return new Companion({
    initial: { ...at, phase },
    standoffRadius: STANDOFF_RADIUS,
    moveSpeed: MOVE_SPEED,
    smoothing: SMOOTHING,
    arrivalRadius: ARRIVAL_RADIUS,
    doorways: [],
    stations: [
      { id: 'lie-up', target: HOLLY_DAWN_LIE_UP },
      { id: 'gateway', target: GATEWAY },
      { id: 'hold', target: HOLLY_HOLD_STATION },
    ],
    bodyRadius: HOLLY_BODY_RADIUS,
    obstacles,
  });
}

export class DawnHolly {
  readonly #obstacles = yardDawnCompanionObstacles();
  readonly #walkable = this.#obstacles.filter((obstacle) => obstacle.id !== DAWN_LEAF_OPEN_ID).map((obstacle) => obstacle.id);
  #companion = companionAt(this.#obstacles, HOLLY_DAWN_LIE_UP, 'following');
  readonly #bell = new CompanionBell({ strideDistance: HOLLY_BELL_STRIDE_METRES, soundRadius: bellEmissionRadius() }, this.#companion.snapshot());
  #phase: DawnHollyPhase = 'lie-up';
  #swing = 0;

  /** Whether `F` would call her now: she is still lying up and Rosalie is at the gate. */
  canCall(rosalie: Vec2): boolean {
    return this.#phase === 'lie-up' && Math.hypot(rosalie.x - RACE_GATE_AT.x, rosalie.z - RACE_GATE_AT.z) <= CALL_RANGE;
  }

  snapshot(): DawnHollySnapshot {
    return { holly: this.#companion.snapshot(), phase: this.#phase, leafProgress: Math.min(1, this.#swing / LEAF_SWING_SECONDS) };
  }

  /**
   * One fixed step. `called` is `F` pressed on this tick. Returns the bell
   * marks she crossed, for the room to voice on the render step.
   *
   * The bell is fed every tick, still ones included — `CompanionBell.advance`
   * rejects a pair that does not continue the trace it last accepted.
   */
  step(dt: number, rosalie: Vec2, called: boolean): readonly CompanionBellEvent[] {
    if (called && this.canCall(rosalie)) this.#phase = 'coming';
    const before = this.#companion.snapshot();
    if (this.#phase === 'coming') {
      // Through the gateway first while she is still at or past the fence line.
      const station = before.z > GATEWAY.z + ARRIVAL_RADIUS ? 'gateway' : 'hold';
      const after = this.#companion.step(dt, rosalie, { atStation: true, stationIds: [station], obstacleIds: this.#walkable });
      if (station === 'hold' && Math.hypot(after.x - HOLLY_HOLD_STATION.x, after.z - HOLLY_HOLD_STATION.z) <= ARRIVAL_RADIUS + TIME_EPSILON) {
        if (!this.#companion.beginHighLatch()) throw new Error('dawn holly: could not begin the rear from following');
        // `#moveToward` leaves her facing the way she walked in; the rear
        // has to be at the leaf (beat 9's `LATCH_YAW` note).
        this.#companion.face(HOLLY_HOLD_STATION.yaw);
        this.#phase = 'rising';
      }
    } else {
      this.#companion.step(dt, rosalie, { atStation: true, stationIds: ['lie-up'], obstacleIds: this.#walkable });
    }
    if (this.#phase === 'rising') {
      this.#swing = Math.min(LEAF_SWING_SECONDS, this.#swing + dt);
      if (this.#swing >= LEAF_SWING_SECONDS - TIME_EPSILON) {
        // Snapped, not left at the float sum: 114 steps of 1/60 land on
        // 0.99999…, and everything downstream (the open collider, mark 2)
        // keys on the progress being exactly 1. The spec caught that.
        this.#swing = LEAF_SWING_SECONDS;
        this.#phase = 'holding';
      }
    }
    return this.#bell.advance(before, this.#companion.snapshot());
  }

  /**
   * The plan's backstop, under black: she is at the gate, holding it. Rebuilt
   * rather than walked, and the bell re-based in the same statement — the
   * two share one trace (`TwelveOut.reset`'s note).
   */
  applyHeld(): void {
    if (this.#phase === 'holding') return;
    this.#companion = companionAt(this.#obstacles, HOLLY_HOLD_STATION, 'at-high-latch');
    this.#bell.reset(this.#companion.snapshot());
    this.#swing = LEAF_SWING_SECONDS;
    this.#phase = 'holding';
  }
}
